/**
 * Turn a `supabase.functions.invoke` failure into something an operator can act
 * on.
 *
 * supabase-js rejects a non-2xx edge-function call with a `FunctionsHttpError`
 * whose `message` is the fixed string "Edge Function returned a non-2xx status
 * code" — the response body, where every one of our functions puts the actual
 * reason (`{ error: "Admin access required" }`, `{ error: "…", code: "…" }`),
 * is left unread on `err.context`. Surfacing only the message turned a
 * one-glance problem into a support thread (feedback 11: an admin rejecting a
 * mandate-cancellation request saw nothing but the generic sentence).
 *
 * The body can only be read once, so `context` is cloned before parsing.
 */
export async function edgeFunctionErrorMessage(
  err: unknown,
  fallback: string
): Promise<string> {
  const ctx = (err as { context?: unknown } | null)?.context;
  if (ctx instanceof Response) {
    try {
      const text = await ctx.clone().text();
      if (text) {
        let detail = text;
        try {
          const parsed = JSON.parse(text) as {
            error?: unknown;
            message?: unknown;
          };
          const picked = parsed?.error ?? parsed?.message;
          if (typeof picked === "string" && picked.trim()) detail = picked;
        } catch {
          // Not JSON (a runtime crash prints plain text) — show it verbatim.
        }
        // The status is what distinguishes "you may not" from "it broke".
        return `${detail.trim().slice(0, 400)} (HTTP ${ctx.status})`;
      }
      return `${fallback} (HTTP ${ctx.status})`;
    } catch {
      return `${fallback} (HTTP ${ctx.status})`;
    }
  }
  const msg = (err as { message?: unknown } | null)?.message;
  return typeof msg === "string" && msg.trim() ? msg : fallback;
}
